import { db, uuid, LexoRank } from '../Database.svelte';
import { readable } from 'svelte/store';
import { sortBy } from 'lodash-es';

function cleanDoc(doc) {
    // clone doc
    let clean_doc = JSON.parse(JSON.stringify(doc))

    delete clean_doc._id
    delete clean_doc._rev

    return clean_doc
}

export class ObjectField {
    constructor(name, model_id, model_type) {
        this.ClassType = this.constructor.name;

        this.name = name;
        this.model_id = model_id;
        this.item_id = uuid();

        this.type = `${model_type}:${name}`;

        this.id = `${this.model_id}:${this.item_id}:${this.name}`

        this._data = {};

        Object.defineProperty(this, "details", {
            get() {
                return this._data;
            }
        })
    }

    get_value() {
        return this._data;
    }

    delete() {
        this._data._deleted = true;
        return db.put(this._data);
    }

    set(value) {
        if (this._data._id) {
            this._data = value;
        } else {
            this._data = {
                _id: this.id,
                type_: this.type,
                item_id: this.item_id,
                ...value
            }
        }

        return db.put(this._data)
    }

    handleDoc(doc, r) {
        this._data = doc;
    }

    toJSON() {
        return cleanDoc(this._data);
    }
}

export function ArrayField(child_name, subtype) {
    if (!subtype) {
        subtype = child_name;
        child_name = "children";
    }
    return class ArrayField_ {
        constructor(name, model_id, model_type) {
            this.name = name;
            this.model_id = `${model_id}:${name}`;
            let type = this.type = `${model_type}:${name}`;

            let id = this.id = `${model_id}:${name}`;

            this.details = {};
            this._data = [];

            this.rank = LexoRank.middle()

            Object.defineProperty(this, child_name, {
                get() {
                    return this._data;
                }
            })

            Object.defineProperties(this, {
                newItem: {
                    get() {
                        return function (val) {
                            let field_id = `${id}`;
                            let item = new subtype(child_name, field_id, type);

                            if (!val.rank_) {
                                val.rank_ = this.rank.toString();
                                this.rank = this.rank.genNext();
                            }

                            return item.set(val);
                        }
                    }
                }
            });
        }

        toJSON() {
            let json = {
                details: cleanDoc(this.details)
            }

            json[child_name] = this._data

            return json
        }

        [Symbol.iterator]() {
            let idx = -1;
            let data = this._data;

            return {
                next: () => ({value: data[++idx], done: !(idx in data)})
            }
        }

        get_value() {
            return this._data
        }


        set(val) {
            if (this.details._id) {
                this.details = val;
            } else {
                this.details = {
                    _id: `${this.id}`,
                    type_: this.type,
                    ...val
                }
                return db.put(this.details);
            }
        }

        moveItem(item, after, before) {
            let beforeRank, afterRank, newRank;
            if (!before) {
                // move item to end of the list
                newRank = this.rank.toString();
                this.rank = this.rank.genNext();
            } else {
                if (!after) {
                    // move to beginning of list
                    afterRank = LexoRank.min();
                } else {
                    afterRank = LexoRank.parse(after.details.rank_);
                }

                beforeRank = LexoRank.parse(before.details.rank_);

                newRank = afterRank.between(beforeRank).toString();
            }

            item.details.rank_ = newRank;

            return item.set(item.details);
        }

        delete() {
            return db.allDocs({
                include_docs: true,
                startkey: this.id,
                endkey: `${this.id}:\uFFF0`
            }).then((result) => {
                let docs_to_delete = [];
                for (let row of result.rows) {
                    row.doc._deleted = true;
                    docs_to_delete.push(row.doc);
                }

                return db.bulkDocs(docs_to_delete);
            })
        }

        handleDoc(doc, r) {
            if (doc._id == this.id) {
                this.details = doc;
            } else {
                let [item_id, field_name, ...rest] = r;

                let idx = this._data.length - 1;

                let item;
                let model_id = `${this.id}:${item_id}`;
                let item_model_id = `${this.id}:${item_id}`;

                if (!doc.rank_) {
                    doc.rank_ = this.rank.toString();
                    this.rank = this.rank.genNext();
                } else {
                    if (doc.rank_ >= this.rank.toString()) {
                        this.rank = LexoRank.parse(doc.rank_).genNext();
                    }
                }

                // if there are no items in the array
                if (idx == -1) {
                    item = new subtype(child_name, model_id, this.type);

                    item.handleDoc(doc, rest);
                    this._data.push(item);
                } else {
                    let found = false;
                    for (let i = 0; i < this._data.length; i++) {
                        if (this._data[i].model_id == item_model_id) {
                            if (doc._deleted) {
                                this._data.splice(i, 1);
                            } else {
                                this._data[i].handleDoc(doc, rest);
                            }

                            found = true;
                            break
                        }
                    }

                    if (!found) {
                        item = new subtype(child_name, model_id, this.type);
                        item.handleDoc(doc, rest);
                        this._data.push(item)
                    }
                }
                this._data = sortBy(this._data, item => item.details.rank_)
            }
        }
    }
}

export function MappingField(fields) {
    return class MappingField_ {
        constructor (name, model_id, model_type) {
            this.name = name;
            this.model_id = `${model_id}:${name}`;
            this.type = `${model_type}:${name}`;


            this.id = `${model_id}:${name}`;

            this.details = {};
            this._data = {};

            this.fields = fields;
        }

        get(key) {
            if (!this._data[key]) {
                this._data[key] = {};

                for (let field of Object.keys(this.fields)) {
                    this._data[key][field] = new this.fields[field](field, `${this.id}:${key}`, this.type);

                    if (!this.hasOwnProperty(key)) {
                        Object.defineProperty(this, key, {
                            get() {
                                return this._data[key];
                            }
                        })
                    }

                }
            }

            return this._data[key]
        }

        delete() {
            return db.allDocs({
                include_docs: true,
                startkey: this.id,
                endkey: `${this.id}:\uFFF0`
            }).then((result) => {
                let docs_to_delete = [];
                for (let row of result.rows) {
                    row.doc._deleted = true;
                    docs_to_delete.push(row.doc);
                }

                return db.bulkDocs(docs_to_delete);
            })
        }

        handleDoc(doc, r) {
            if (doc._id == this.id) {
                this.details = doc;
            } else {
                let [key, field_name, ...rest] = r;

                if (!this._data[key]) {
                    this._data[key] = {};

                    for (let field of Object.keys(this.fields)) {
                        this._data[key][field] = new this.fields[field](field, this.id, this.type);
                    }

                    Object.defineProperty(this, key, {
                        get() {
                            return this._data[key];
                        }
                    })
                }

                this._data[key][field_name].handleDoc(doc, rest);

            }
        }
    }
}


export class ORM {
    static fields = {};
    static collectionType = "";

    constructor(id) {
        this.id = id;
        this._data = {};

        this._details = {};

        for (let field of Object.keys(this.constructor.fields)) {
            if (field == "details") throw new Error("details is a reserved field")

            this._data[field] = new this.constructor.fields[field](field, this.id, this.constructor.collectionType);

            Object.defineProperty(this, field, {
                get() {
                    return this._data[field]
                }
            });
        }

        Object.defineProperty(this, "store", {
            get() {
                return this.getStore()
            }
        });

        Object.defineProperty(this, "details", {
            get() {
                return this._details;
            }
        });
    }

    static createItem(details) {
        if (!this.collectionType) {
            throw new Error("Database collection name is required")
        }

        let id = uuid();
        let doc = {
            _id: `${this.collectionType}:${id}`,
            item_id: id,
            type: this.collectionType,
            ...details
        }

        return db.put(doc);
    }

    toJSON() {
        return {
            details: cleanDoc(this._details),
            ...this._data
        }
    }

    delete() {
        return db.allDocs({
            include_docs: true,
            startkey: this.id,
            endkey: `${this.id}:\uFFF0`
        }).then((result) => {
            let docs_to_delete = [];
            for (let row of result.rows) {
                row.doc._deleted = true;
                docs_to_delete.push(row.doc);
            }

            return db.bulkDocs(docs_to_delete);
        })
    }

    static getList() {
        let item_list = [];

        return readable(undefined, (set) => {
            db.find({
                selector: {
                    type: this.collectionType
                }
            }).then(results => {
                item_list = results.docs;
                set(item_list)
            });

            let changeMonitor = db.changes({
                since: "now",
                live: true,
                include_docs: true,
                filter: "filters/doc_type",
                query_params: {
                    type: this.collectionType
                }
            }).on("change", (change) => {
                if (change.deleted) {
                    item_list = item_list.filter(item => item._id != change.id)
                } else {
                    let found = false;
                    item_list = item_list.map(item => {
                    if (item._id != change.id) return item;
                        found = true;
                        return change.doc
                    })

                    if (!found) {
                        item_list.push(change.doc);
                    }
                }

                set(item_list);
            })

            return () => {
                changeMonitor.cancel();
            }
        });
    }

    static async getStaticList() {
        return db.find({
            selector: {
                type: this.collectionType
            }
        }).then(results => {
            return results.docs;
        });
    }

    static save(doc) {
        return db.put(doc);
    }

    save(doc) {
        return db.put(doc);
    }

    handleDoc(doc) {
        if (doc._id == this.id) {
            this._details = doc;
        } else {
            let [model_type, model_id, field_name, ...rest] = doc._id.split(":");

            this._data[field_name].handleDoc(doc, rest);
        }

    }

    getStore() {
        return readable(undefined, (set) => {
            db.allDocs({
                include_docs: true,
                startkey: `${this.id}`,
                endkey: `${this.id}:\ufff0`
            }).then(result => {
                for (let row of result.rows) {
                    this.handleDoc(row.doc);
                }
                set(this);
            });

            let changeMonitor = db.changes({
                since: 'now',
                live: true,
                include_docs: true,
                filter: "filters/starts_with",
                query_params: {
                    id: this.id
                }
            }).on("change", (change) => {
                this.handleDoc(change.doc);

                set(this);
            });

            return () => {
                changeMonitor.cancel();
            };
        })
    }
}